import { Select, SelectItem, Toggle } from '@reelplicate/ui';
import { type ReactNode, useId } from 'react';

import { SettingsRow } from './settings-layout';

/**
 * A Settings Row with a Toggle: applies instantly (Figma: "Toggles apply
 * instantly"). Uncontrolled unless `checked` is passed.
 */
export function SettingToggle({
  label,
  description,
  badge,
  ...toggle
}: {
  label: string;
  description?: ReactNode;
  badge?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
}) {
  const labelId = useId();
  return (
    <SettingsRow
      label={label}
      labelId={labelId}
      badge={badge}
      description={description}
      disabled={toggle.disabled}
      control={<Toggle aria-labelledby={labelId} {...toggle} />}
    />
  );
}

/**
 * A Settings Row with a 200px Small Select. `options` are [value, label].
 * Uncontrolled with `defaultValue`, or controlled with `value`.
 */
export function SettingSelect({
  label,
  description,
  options,
  defaultValue,
  value,
  onValueChange,
  disabled,
}: {
  label: string;
  description?: ReactNode;
  options: [string, string][];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <SettingsRow
      label={label}
      description={description}
      disabled={disabled}
      control={
        <Select
          size="sm"
          className="w-50"
          defaultValue={defaultValue}
          value={value}
          onValueChange={onValueChange}
          disabled={disabled}
          aria-label={label}
        >
          {options.map(([value, text]) => (
            <SelectItem key={value} value={value}>
              {text}
            </SelectItem>
          ))}
        </Select>
      }
    />
  );
}
